import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../util/routes';
import { createSlug } from '../../../util/urlHelpers';
import { obfuscatedCoordinates } from '../../../util/maps';

import SearchMapInfoCard from '../SearchMapInfoCard/SearchMapInfoCard';

import css from './SearchMapClustered.module.css';

const MAP_STYLE = 'mapbox://styles/mapbox/streets-v11';
const CLUSTER_MAX_ZOOM = 14;
const CLUSTER_RADIUS = 50;
const FIT_MAX_ZOOM = 12;
const FIT_PADDING = 60;
const SOURCE_ID = 'r2r-pickup-listings';
const EMPTY_FEATURE_COLLECTION = { type: 'FeatureCollection', features: [] };

const getMapboxGl = () => (typeof window !== 'undefined' ? window.mapboxgl : null);

// Resolve the coordinate to render the marker at, applying fuzzy obfuscation when enabled.
const markerCoordinate = (listing, fuzzyConfig) => {
  const geolocation = listing?.attributes?.geolocation;
  if (!geolocation) {
    return null;
  }
  if (fuzzyConfig?.enabled) {
    const cacheKey = `${listing.id.uuid}_${geolocation.lat}_${geolocation.lng}`;
    return obfuscatedCoordinates(geolocation, fuzzyConfig.offset, cacheKey);
  }
  return geolocation;
};

// Build a GeoJSON FeatureCollection from the listing entities.
const listingsToGeoJSON = (listings, fuzzyConfig) => {
  const features = listings.reduce((acc, listing) => {
    const coordinate = markerCoordinate(listing, fuzzyConfig);
    if (!coordinate) {
      return acc;
    }
    acc.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [coordinate.lng, coordinate.lat] },
      properties: { listingId: listing.id.uuid },
    });
    return acc;
  }, []);
  return { type: 'FeatureCollection', features };
};

/**
 * Map of all pickup-enabled listings using Mapbox GL native clustering.
 *
 * Unlike the stock SearchMap (one DOM marker per coordinate group), this renders the markers via
 * a clustered GeoJSON source so it scales to hundreds of listings. Clusters zoom in on click; an
 * individual point opens the reused SearchMapInfoCard. The map auto-fits to the dataset and does
 * not trigger a new search when panned/zoomed.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {Array<propTypes.listing>} [props.listings] - Listing entities (with geolocation)
 * @param {Function} [props.onActivateListing] - Optional active-listing setter
 * @returns {JSX.Element}
 */
const SearchMapClusteredMapbox = props => {
  const { className, rootClassName, listings = [], onActivateListing } = props;

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const listingsRef = useRef(listings);
  listingsRef.current = listings;

  const [isStyleReady, setIsStyleReady] = useState(false);
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [popupContainer, setPopupContainer] = useState(null);

  const fuzzyConfig = config.maps?.fuzzy;

  const createURLToListing = listing => {
    const id = listing.id.uuid;
    const slug = createSlug(listing.attributes.title);
    return createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {});
  };

  const onListingInfoCardClicked = listing => {
    history.push(createURLToListing(listing));
  };

  // Initialize the map once the Mapbox GL library is available AND the container has a non-zero
  // size. The map library is deferred on the search route, and the container is rendered inside a
  // modal/flex wrapper, so we retry until both conditions are met (otherwise the canvas would
  // initialize at 0x0 and stay blank). A ResizeObserver keeps the canvas sized to its container.
  useEffect(() => {
    let cancelled = false;
    let pollId = null;
    let resizeObserver = null;

    const addLayersAndHandlers = map => {
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: EMPTY_FEATURE_COLLECTION,
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: CLUSTER_RADIUS,
      });

      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: SOURCE_ID,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#2eb6eb',
          'circle-opacity': 0.9,
          'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 30],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      });

      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: SOURCE_ID,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 13,
        },
        paint: { 'text-color': '#ffffff' },
      });

      map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: SOURCE_ID,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#1a8fbf',
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      map.on('click', 'clusters', e => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = features[0]?.properties?.cluster_id;
        const source = map.getSource(SOURCE_ID);
        if (clusterId == null || !source) {
          return;
        }
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) {
            return;
          }
          map.easeTo({ center: features[0].geometry.coordinates, zoom });
        });
      });

      map.on('click', 'unclustered-point', e => {
        const listingId = e.features?.[0]?.properties?.listingId;
        if (listingId) {
          setSelectedListingId(listingId);
          if (onActivateListing) {
            onActivateListing(listingId);
          }
        }
      });

      // Dismiss the listing card when tapping empty map. Layer clicks on
      // clusters / unclustered-point also bubble to map `click`, so skip those.
      map.on('click', e => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['clusters', 'cluster-count', 'unclustered-point'],
        });
        if (features.length === 0) {
          setSelectedListingId(null);
        }
      });

      const setPointerCursor = () => {
        map.getCanvas().style.cursor = 'pointer';
      };
      const clearCursor = () => {
        map.getCanvas().style.cursor = '';
      };
      map.on('mouseenter', 'clusters', setPointerCursor);
      map.on('mouseleave', 'clusters', clearCursor);
      map.on('mouseenter', 'unclustered-point', setPointerCursor);
      map.on('mouseleave', 'unclustered-point', clearCursor);
    };

    const tryInit = () => {
      if (cancelled || mapRef.current) {
        return true;
      }
      const mapboxgl = getMapboxGl();
      const container = mapContainerRef.current;
      if (!mapboxgl || !container) {
        return false;
      }
      // Wait until the container has been laid out; mapbox-gl sizes the canvas at init time.
      if (container.offsetWidth === 0 || container.offsetHeight === 0) {
        return false;
      }

      // Defensively ensure the access token is set (normally done in includeScripts).
      if (!mapboxgl.accessToken && config.maps?.mapboxAccessToken) {
        mapboxgl.accessToken = config.maps.mapboxAccessToken;
      }

      const map = new mapboxgl.Map({
        container,
        style: MAP_STYLE,
        center: [0, 20],
        zoom: 1,
      });
      mapRef.current = map;
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');

      map.on('load', () => {
        if (cancelled) {
          return;
        }
        addLayersAndHandlers(map);
        setIsStyleReady(true);
      });

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          if (mapRef.current) {
            mapRef.current.resize();
          }
        });
        resizeObserver.observe(container);
      }

      return true;
    };

    if (!tryInit()) {
      pollId = setInterval(() => {
        if (tryInit()) {
          clearInterval(pollId);
          pollId = null;
        }
      }, 150);
    }

    return () => {
      cancelled = true;
      if (pollId) {
        clearInterval(pollId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push listing data into the source and auto-fit the viewport whenever the dataset changes.
  useEffect(() => {
    const map = mapRef.current;
    const mapboxgl = getMapboxGl();
    if (!map || !isStyleReady || !mapboxgl) {
      return;
    }

    const geojson = listingsToGeoJSON(listings, fuzzyConfig);
    const source = map.getSource(SOURCE_ID);
    if (source) {
      source.setData(geojson);
    }

    if (geojson.features.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      geojson.features.forEach(feature => bounds.extend(feature.geometry.coordinates));
      map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, duration: 0 });
    }
  }, [listings, isStyleReady, fuzzyConfig]);

  // Manage the info-card popup for the selected listing.
  useEffect(() => {
    const map = mapRef.current;
    const mapboxgl = getMapboxGl();

    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
    setPopupContainer(null);

    if (!map || !mapboxgl || !selectedListingId) {
      return undefined;
    }

    const listing = listingsRef.current.find(l => l.id.uuid === selectedListingId);
    const coordinate = markerCoordinate(listing, fuzzyConfig);
    if (!coordinate) {
      return undefined;
    }

    const container = document.createElement('div');
    // closeOnClick is false: a default-true close would fire popup 'close' while a
    // pin handler is also setting a new listing id, wiping the card on pin-to-pin taps.
    // Empty-map dismiss is handled by the feature-aware map click listener above.
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 18,
      maxWidth: '260px',
    })
      .setLngLat([coordinate.lng, coordinate.lat])
      .setDOMContent(container)
      .addTo(map);

    popupRef.current = popup;
    setPopupContainer(container);

    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    };
  }, [selectedListingId, fuzzyConfig]);

  const selectedListing = selectedListingId
    ? listings.find(l => l.id.uuid === selectedListingId)
    : null;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div ref={mapContainerRef} className={css.mapContainer} />
      {popupContainer && selectedListing
        ? ReactDOM.createPortal(
            <SearchMapInfoCard
              listings={[selectedListing]}
              createURLToListing={createURLToListing}
              onListingInfoCardClicked={onListingInfoCardClicked}
              onClose={() => setSelectedListingId(null)}
              config={config}
            />,
            popupContainer
          )
        : null}
    </div>
  );
};

export default SearchMapClusteredMapbox;
